import { getCountryName } from "~/lib/helpers/constants";
import WorkspaceFinder from "../Home_Page/WorkspaceFinder";
import ModernHeroSection from "./ModernHeroSection";
import WhyOfficeHub from "../Home_Page/WhyOfficeHub";
import AmenitiesSection from "./AmenitiesSection";
import Footer from "../Footer";
import Container from "~/components/atoms/Container";
import { GoogleReviews } from "../Home_Page/GoogleReviews/GoogleReviews";
import FAQs from "../FAQs/FAQs";
import React, { Suspense, useMemo, useState, lazy, useRef } from "react";
import { Await, useNavigation, Link } from "@remix-run/react";
import Text from "~/components/atoms/Text";

import { PropertyData, Workspace } from "../Geosearch/interfaces";
import NoResults from "../Geosearch/NoResult";
import SearchBar from "../Searchbars/AreaSearchbar";
import PGridCard from "../Card/PGridCard";
import { buildCardListingDisplayFromListing } from "~/lib/helpers/cardListingDisplay";
import { listingSlug, propertySlug, buildingSlug, workspaceSlug, getRegionUrlPrefix, getRegionUrlPrefixWithCountryCode } from "~/lib/helpers/slugHelper";
import { OhButton } from "~/components/ui/custom-radix-components/button";
import { Loader2 } from "lucide-react";
import OfficeSearchBanner from "../common/OfficeBanner";
import { toProperCase } from "~/lib/helpers/StringHelper";
import { getRegionConfigByCountry } from "custom-config/region.configs";

const FilterDrawer = lazy(() => import("../Geosearch/Drawer"));

interface DesktopLayoutProps {
    countryName: string;
    region: string;
    loaderData: any;
    city?: string;
    onContinue?: () => void;
    onBack?: () => void;
    step?: number;
    isMobile?: boolean;
    faqs: any;
    // Geo search props
    query?: string;
    filterData?: any;
    callGeoSearch?: Function;
    callGeoSearchForDrawer?: Function;
    onDrawerApply?: Function;
    setFilterData?: Function;
    geoData?: any;
    selectedSort?: { label: string; value: string };
    setSelectedSort?: Function;
    pathname?: string;
    range?: any;
    onLoadMore?: () => void;
    hasMore?: boolean;
    loading?: boolean;
}

export default function DesktopLayout({
    countryName,
    region,
    loaderData,
    city,
    onContinue,
    onBack,
    step = 1,
    isMobile = false,
    faqs,
    // Geo search props
    query = "",
    filterData = {},
    callGeoSearch = () => { },
    callGeoSearchForDrawer = () => { },
    onDrawerApply = () => { },
    setFilterData = () => { },
    geoData,
    selectedSort = { label: '', value: '' },
    setSelectedSort = () => { },
    pathname = "",
    range,
    onLoadMore,
    hasMore,
    loading,
}: DesktopLayoutProps) {

    const [displayMode, setDisplayMode] = useState("list");
    const [isOpen, setIsOpen] = useState(false);
    const [rightWidth, setRightWidth] = useState(45);
    const navigation = useNavigation();
    const toggleDrawer: VoidFunction = () => {
        setIsOpen((prevState) => !prevState);
    };

    const data = geoData;

    // Helper for rendering property cards
    const renderPropertyCard = (b: PropertyData, isNearby = false) => {
        const link = isNearby
            ? listingSlug(b as any)
            : propertySlug(b as any);

        const propertyLink = propertySlug(({ ...b, id: b.propertyId } as any));

        const buildingLink = b?.buildingData
            ? buildingSlug(b.buildingData as any)
            : "";

        const workspaceLink = b?.workspaceData
            ? workspaceSlug(b.workspaceData as any)
            : "";

        const listingRecord = {
            ...b,
            assets: b?.assets?.length
                ? b.assets
                : (b as any)?.imageUrl
                    ? [{ url: (b as any).imageUrl }]
                    : [],
            buildingName: b.buildingName || b.name,
            priceMonthly: b.priceMonthly ?? (b as any).avgMonthlyCostPerWorkstation,
        };

        const display = buildCardListingDisplayFromListing(
            listingRecord,
            { isProperty: true }
        );

        return (
            <React.Fragment key={`card-${b.id}`}>
                <PGridCard
                    {...display}
                    link={link}
                    propertyLink={propertyLink}
                    buildingLink={buildingLink}
                    workspaceLink={workspaceLink}
                    loading={navigation.state === "loading"}
                    isFirstVisible={false}
                    altText={b.addressFormula}
                    titleText={b.buildingName || b.name}
                    isPropertyPage={true}
                    showFavoriteButton={false}
                />
            </React.Fragment>
        );
    };

    const renderNearbyListings = useMemo(() => {
        if (data && data?.data?.autoReCalibratedData?.length) {
            return data.data.autoReCalibratedData.map((b: PropertyData) =>
                renderPropertyCard(b, true)
            );
        }
    }, [data, navigation.state]);

    const renderCards = useMemo(() => {

        if (data && data?.data?.data?.length) {
            return data.data.data.map((b: PropertyData) =>
                renderPropertyCard(b)
            );
        } else if (data) {
            return (
                <>
                    <div className="col-span-full w-full mb-4">
                        <NoResults query={query} params={data.params} />
                        {data?.data?.autoReCalibratedData?.length ? <div className="mt-5"><Text className='text-base font-semibold'>Other options in and around {city}</Text></div> : null}
                    </div>
                    {renderNearbyListings}
                    {data?.data?.autoReCalibratedData?.length ? <div className="col-span-full w-full flex justify-center my-5">
                        <OhButton variant="destructive" className="mx-auto"
                            onClick={() => {
                                callGeoSearch({ latitude: data.data.autoReCalibratedData[0].geolocationLat, longitude: data.data.autoReCalibratedData[0].geolocationLong, zoom: 14 })
                            }}>Explore All Options</OhButton>
                    </div> : null}
                </>
            );
        }
        return null;
    }, [data, navigation.state, query, city, renderNearbyListings]);

    const hasListings = data && data?.data?.data?.length > 0;

    return (
        <div className="flex flex-col w-full justify-center montserrat">
            <ModernHeroSection
                countryName={countryName}
                region={region}
                loaderData={loaderData}
                city={city}
                onContinue={onContinue}
                onBack={onBack}
                step={step}
                isCampaignPage={true}
                isMobile={isMobile}
            />
            <WorkspaceFinder region={region} country={getCountryName(region)} />
            <WhyOfficeHub region={region} country={getCountryName(region)} isMobile={false} />

            {/* ========================================
                GEO SEARCH LISTINGS SECTION
                ======================================== */}
            {data && (
                <Container htmlFor="ultra-wide">
                    <div style={{ marginBottom: 28 }}>
                        {/* Filter Drawer */}
                        {isOpen ? (
                            <Suspense fallback={null}>
                                <FilterDrawer
                                    open={isOpen}
                                    setOpen={setIsOpen}
                                    data={data}
                                    filterData={filterData}
                                    callGeoSearch={callGeoSearchForDrawer}
                                    workspace={null as any}
                                    onApply={onDrawerApply}
                                    maxPrice={range}
                                    isCampaignPage={true}
                                />
                            </Suspense>
                        ) : null}

                        <div className="lg:flex relative pt-4 gap-[28px]">
                            {/* Left Section - Listings */}
                            <div className="transition-all duration-300 flex-[5] pt-[10px] min-h-[calc(100vh-136px)] min-w-[375px] w-full">
                                <div className="flex items-start justify-between w-full">
                                    <div className='flex flex-col w-full'>
                                        <Text className='text-xl text-oh-black font-semibold my-4'>
                                            Office Spaces in {((city && toProperCase(city)) || countryName)}
                                        </Text>

                                        <SearchBar
                                            toggleDrawer={toggleDrawer}
                                            setWidth={setRightWidth}
                                            rightWidth={rightWidth}
                                            setDisplayMode={setDisplayMode}
                                            filterData={filterData}
                                            callGeoSearch={callGeoSearch}
                                            setFilterData={setFilterData}
                                            query={query}
                                            workspace={null as any}
                                            displayMode={displayMode}
                                            setSelectedSort={setSelectedSort}
                                            selectedSort={selectedSort as any}
                                            isCampaignPage={true}
                                        />
                                        {hasListings ? (
                                            <Text className='text-sm font-normal text-gray-800 my-4'>
                                                Showing 1 - {data.data.visibleProperties || data.data.data.length} out of {data.data.totalProperties || data.data.totalListings} spaces
                                            </Text>
                                        ) : null}
                                    </div>
                                </div>
                                <div
                                    className='mt-2'
                                    id="campaign-listings-container"
                                >
                                    <div
                                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3 pb-4"
                                    >
                                        {renderCards}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* View More Button */}
                        {hasListings && hasMore && (
                            <div className="flex justify-center my-8 w-full">
                                <OhButton
                                    className="w-full sm:w-auto min-w-[200px]"
                                    onClick={onLoadMore}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Loading...
                                        </>
                                    ) : (
                                        "Load more"
                                    )}
                                </OhButton>
                            </div>
                        )}
                    </div>
                </Container>
            )}

            <AmenitiesSection region={region} />
            <div id="reviews">
                <Container htmlFor="ultra-wide">
                    <GoogleReviews
                        country={getCountryName(region)}
                        region={region}
                        isMobile={false}
                        hideViewAllReviews={true}
                    />
                </Container>
            </div>
            <Suspense fallback={<div className="h-40 w-full bg-gray-50 rounded-lg" />}>
                <Await resolve={faqs}>
                    {(resolvedFaqs) => (
                        <Container htmlFor="ultra-wide" className="my-10">
                            <FAQs
                                faqs={resolvedFaqs}
                                title={`Answers to Common Office Space Queries in ${((city && toProperCase(city)) || countryName)}`}
                            />
                        </Container>
                    )}
                </Await>
            </Suspense>

            <OfficeSearchBanner
                isCampaignPage={true}
                country={region}
                description={`Tell us what you need and we’ll shortlist the best available offices across ${city}, saving you days of research and back-and-forth.`}
            />

            <Footer region={region} isMobile={false} isCampaignPage={true} />
        </div>
    );
}